import { useEffect, useState } from "react";
import { Eye, Filter } from "lucide-react";

import AuditDetailDrawer from "@/components/AuditDetailDrawer";
import { useCurrency } from "@/context/CurrencyContext";
import { api } from "@/lib/api";
import { formatBoliviaDate } from "@/lib/dateTime";

const initialFilters = {
  start_date: "",
  end_date: "",
  seller_id: "",
  client_id: "",
  lot_id: "",
  rule_id: "",
};

export default function QuoteAuditPanel() {
  const { formatAmount } = useCurrency();
  const [filters, setFilters] = useState(initialFilters);
  const [options, setOptions] = useState({
    vendedores: [],
    clientes: [],
    lotes: [],
    reglas: [],
  });
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadAudit = async () => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value),
    );
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/audit/quotes", { params });
      setQuotes(response.data);
    } catch (requestError) {
      setError("No fue posible cargar el historial de auditoría.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialAudit = async () => {
      setLoading(true);
      try {
        const response = await api.get("/audit/quotes");
        setQuotes(response.data);
      } catch (requestError) {
        setError("No fue posible cargar el historial de auditoría.");
      } finally {
        setLoading(false);
      }
    };

    api
      .get("/audit/filter-options")
      .then((response) => setOptions(response.data))
      .catch(() => setError("No fue posible cargar los filtros de auditoría."));
    loadInitialAudit();
  }, []);

  const setFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  return (
    <>
      <section className="audit-filters" data-testid="audit-filters-panel">
        <label htmlFor="audit-start">
          Desde
          <input
            data-testid="audit-start-date-input"
            id="audit-start"
            onChange={(event) => setFilter("start_date", event.target.value)}
            type="date"
            value={filters.start_date}
          />
        </label>
        <label htmlFor="audit-end">
          Hasta
          <input
            data-testid="audit-end-date-input"
            id="audit-end"
            onChange={(event) => setFilter("end_date", event.target.value)}
            type="date"
            value={filters.end_date}
          />
        </label>
        <label htmlFor="audit-seller">
          Asesor
          <select
            data-testid="audit-seller-select"
            id="audit-seller"
            onChange={(event) => setFilter("seller_id", event.target.value)}
            value={filters.seller_id}
          >
            <option value="">Todos</option>
            {options.vendedores.map((item) => (
              <option key={item.id} value={item.id}>{item.nombre}</option>
            ))}
          </select>
        </label>
        <label htmlFor="audit-client">
          Cliente
          <select
            data-testid="audit-client-select"
            id="audit-client"
            onChange={(event) => setFilter("client_id", event.target.value)}
            value={filters.client_id}
          >
            <option value="">Todos</option>
            {options.clientes.map((item) => (
              <option key={item.id} value={item.id}>{item.nombre}</option>
            ))}
          </select>
        </label>
        <label htmlFor="audit-lot">
          Lote
          <select
            data-testid="audit-lot-select"
            id="audit-lot"
            onChange={(event) => setFilter("lot_id", event.target.value)}
            value={filters.lot_id}
          >
            <option value="">Todos</option>
            {options.lotes.map((item) => (
              <option key={item.id} value={item.id}>{item.nombre}</option>
            ))}
          </select>
        </label>
        <label htmlFor="audit-rule">
          Regla aplicada
          <select
            data-testid="audit-rule-select"
            id="audit-rule"
            onChange={(event) => setFilter("rule_id", event.target.value)}
            value={filters.rule_id}
          >
            <option value="">Todas</option>
            {options.reglas.map((item) => (
              <option key={item.id} value={item.id}>{item.nombre}</option>
            ))}
          </select>
        </label>
        <button
          className="dark-button audit-filter-button"
          data-testid="audit-filter-button"
          disabled={loading}
          onClick={loadAudit}
          type="button"
        >
          <Filter size={17} />
          {loading ? "Actualizando..." : "Filtrar historial"}
        </button>
      </section>
      {error && <p className="form-error" data-testid="audit-error-message">{error}</p>}
      <section className="audit-table-panel" data-testid="audit-table-panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">REGISTROS</span>
            <h2>Cotizaciones auditadas</h2>
          </div>
          <span data-testid="audit-count">{quotes.length} registros</span>
        </div>
        <div className="data-table-wrap">
          <table data-testid="audit-table">
            <thead>
              <tr>
                <th>Fecha y asesor</th>
                <th>Cliente</th>
                <th>Lote</th>
                <th>Condición</th>
                <th>Reglas y descuento</th>
                <th>Saldo final</th>
                <th>Observaciones</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((quote) => (
                <tr data-testid={`audit-row-${quote.id}`} key={quote.id}>
                  <td>
                    <strong>{formatBoliviaDate(quote.created_at)}</strong>
                    <small>{quote.vendedor}</small>
                  </td>
                  <td>
                    <strong>{quote.client.nombre}</strong>
                    <small>CI {quote.client.ci}</small>
                  </td>
                  <td>Manzano {quote.lot.manzano} · Lote {quote.lot.lote}</td>
                  <td>
                    {quote.inicial_porcentaje}% inicial
                    <small>{quote.meses ? `${quote.meses} meses` : "Contado"}</small>
                  </td>
                  <td>
                    <strong>− {formatAmount(quote.descuento)}</strong>
                    <small>
                      {quote.reglas_aplicadas
                        ?.map((rule) => `${rule.nombre} (${rule.porcentaje}%)`)
                        .join(", ") || "Sin regla"}
                    </small>
                  </td>
                  <td>{formatAmount(quote.saldo)}</td>
                  <td>{quote.observaciones || "—"}</td>
                  <td>
                    <button
                      aria-label={`Ver detalle de cotización ${quote.id}`}
                      className="icon-button audit-view-button"
                      data-testid={`view-audit-quote-${quote.id}`}
                      onClick={() => setSelectedQuote(quote)}
                      type="button"
                    >
                      <Eye size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && !quotes.length && (
          <p className="empty-inline" data-testid="audit-empty-state">
            No hay cotizaciones para los filtros seleccionados.
          </p>
        )}
      </section>
      {selectedQuote && (
        <AuditDetailDrawer
          detail={selectedQuote}
          onClose={() => setSelectedQuote(null)}
          title={`Cotización · ${selectedQuote.client.nombre}`}
        />
      )}
    </>
  );
}